import { TitleCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    inject,
    input,
    OnDestroy,
    signal,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { ExpenseTypeEnum } from '@app/enums/ExpenseTypes';
import { RequestTypes, RequestTypesKeys } from '@app/enums/RequestTypes';
import { getTravelRequestEnumStringValue, TravelRequestEnum } from '@app/enums/travel-request.enum';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { AgGridStatusBarComponent } from '@app/v2/components/ag-grid-status-bar/ag-grid-status-bar.component';
import {
    DuplicateDialogComponent,
    DuplicateDialogData,
    DuplicateDialogResult,
} from '@app/v2/components/dialogs/duplicate-dialog/duplicate-dialog.component';
import {
    MessageDialogComponent,
    MessageDialogData,
} from '@app/v2/components/dialogs/message-dialog/message-dialog.component';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { toExchangeRateDate } from '@app/v2/services/currency-exchange-rate/currency-exchange-rate.service';
import { BaseCellEditorComponent } from '@components/ag-grid/cell-editors/base-cell-editor/base-cell-editor.component';
import { CustomCellEditorButtonComponent } from '@components/ag-grid/cell-editors/custom-cell-editor-button/custom-cell-editor-button.component';
import { CustomCellEditorDatepickerComponent } from '@components/ag-grid/cell-editors/custom-cell-editor-datepicker/custom-cell-editor-datepicker.component';
import { CustomCellEditorDropdownComponent } from '@components/ag-grid/cell-editors/custom-cell-editor-dropdown/custom-cell-editor-dropdown.component';
import { CustomCellEditorInputComponent } from '@components/ag-grid/cell-editors/custom-cell-editor-input/custom-cell-editor-input.component';
import { BaseCellRendererComponent } from '@components/ag-grid/cell-renderers/base-cell-renderer/base-cell-renderer.component';
import { CustomCellRenderIconComponent } from '@components/ag-grid/cell-renderers/custom-cell-render-icon/custom-cell-render-icon.component';
import { CustomCellRenderImgComponent } from '@components/ag-grid/cell-renderers/custom-cell-render-img/custom-cell-render-img.component';
import {
    MdsButtonComponent,
    MdsButtonToggle,
    MdsButtonToggleGroupComponent,
    MdsDateRangePickerComponent,
    MdsDividerComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import {
    type CellEditorSelectorResult,
    CellEditRequestEvent,
    type CellRendererSelectorResult,
    type ColDef,
    Column,
    ColumnState,
    ExcelExportParams,
    ExcelStyle,
    FilterModel,
    GridApi,
    GridOptions,
    GridPreDestroyedEvent,
    GridReadyEvent,
    ICellRendererParams,
    IMenuActionParams,
    IRowNode,
    MdsAgGridComponent,
    ProcessCellForExportParams,
    RowDataTransaction,
    ServerSideTransaction,
    SetFilterModel,
    StartEditingCellParams,
} from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';
import { BatchEditAction } from '@models/batch-edit-action';
import { BatchSetAddExpense, BatchSetAddResponse } from '@models/batch-set-add-expense';
import { CreateEditSETDataRowDto } from '@models/CreateEditSETDataRowDto';
import { CurrencyViewModel } from '@models/currency';
import { DateFormat } from '@models/date-format';
import { ExpenseIdentifier } from '@models/expense-identifier';
import {
    LiveNotificationsActionEnum,
    RequestChangedNotification,
} from '@models/live-notifications/request-changed-notification.model';
import { SetQuery } from '@models/queries/set-query';
import { PaymentRequestStatus, RequestStatusNames, TravelRequestStatus } from '@models/request';
import { StudyExpenseTrackingColumnId } from '@models/set-col-id';
import { SetRowInfo } from '@models/set-row-info';
import { StudyExpense, VisitName } from '@models/study';
import { StudyExpenseTrackingDataRow, StudyExpenseTrackingDataRowDto } from '@models/studyExpenseTrackingDataRow';
import { ExpenseTrackingService } from '@services/expense-tracking/expense-tracking.service';
import { buildRequestSetGroup } from '@services/signalr/notification-group.util';
import { NotificationHubService } from '@services/signalr/notification-hub.service';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { UndoRedoService } from '@services/undo-redo/undo-redo.service';
import {
    emptyMerchantOption,
    emptyVisitName,
    maxDateValue,
    maxNumberValue,
    minDateValue,
    minNumberValue,
} from '@utility/constants';
import { guidGenerator } from '@utility/guid-generator';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { dateTransform, MdsOptionGeneric } from '@utility/utility';
import { afterDateValidator, beforeDateValidator } from '@utility/utility.validators';
import { compare, deepClone } from 'fast-json-patch';
import moment from 'moment';

import { ConvertAuthorizedAmountRequest, PotentialDuplicateRequestDto } from '@app/generated-api';
import { AgGridDefaults } from '@app/v2/utils/ag-grid/ag-grid-defaults';
import { PaymentRequestStatusChangeModalCellEditorComponent } from '@components/ag-grid/cell-editors/payment-request-status-change-modal-cell-editor/payment-request-status-change-modal-cell-editor.component';
import { TravelRequestStatusChangeModalCellEditorComponent } from '@components/ag-grid/cell-editors/travel-request-status-change-modal-cell-editor/travel-request-status-change-modal-cell-editor.component';
import { SiteService } from '@services/site/site.service';
import {
    catchError,
    combineLatest,
    concatMap,
    EMPTY,
    map,
    Observable,
    of,
    startWith,
    switchMap,
    take,
    takeWhile,
    tap,
    timer,
} from 'rxjs';
import {
    getRequestIdentifier,
    isNotInFinalStatus,
    isNotMileage,
    isPayment,
    isPaymentBeforePendingPSApproval,
    isPaymentReadOnlyStatus,
    isPossibleToAddRow,
    isRequestNotesEditable,
    isTravelWithReceipt,
    parseDateField,
    parseNumberField,
    parseTextField,
} from './extensions';
import { mapToExportValue } from './helpers/excelExportHelper';
import {
    dateComparator,
    expenseTypeComparator,
    localCurrencyComparator,
    requestStatusComparator,
    visitNameComparator,
} from './helpers/sortHelper';

interface CellEditorSelectorResultGeneric<TComponent = any, TParams = any> extends CellEditorSelectorResult {
    /** Equivalent of setting `colDef.cellEditor` */
    component?: new (...args: any[]) => TComponent;
    /** Equivalent of setting `colDef.cellEditorParams` */
    params?: TParams;
    /** Equivalent of setting `colDef.cellEditorPopup` */
    popup?: boolean;
    /** Equivalent of setting `colDef.cellEditorPopupPosition` */
    popupPosition?: 'over' | 'under';
}
interface CellRendererSelectorResultGeneric<TComponent = any, TParams = any> extends CellRendererSelectorResult {
    /** Equivalent of setting `colDef.cellEditor` */
    component?: new (...args: any[]) => TComponent;
    /** Equivalent of setting `colDef.cellEditorParams` */
    params?: TParams;
}
interface StudyExpenseTrackerColDef<
    TValue,
    TCellEditor extends BaseCellEditorComponent<any, any, any, any> = any,
    TCellRenderer extends BaseCellRendererComponent<any, any, any, any> = any,
> extends ColDef<StudyExpenseTrackingDataRow, TValue> {
    cellEditorSelector?: (
        params: ReturnType<TCellEditor['baseParams']>
    ) => CellEditorSelectorResultGeneric<
        TCellEditor,
        Partial<ReturnType<TCellEditor['baseParams']> & ReturnType<TCellEditor['customParams']>>
    >;
    cellRendererSelector?: (
        params: ReturnType<TCellRenderer['baseParams']>
    ) => CellRendererSelectorResultGeneric<
        TCellRenderer,
        Partial<ReturnType<TCellRenderer['baseParams']> & ReturnType<TCellRenderer['customParams']>>
    >;
}

@Component({
    selector: 'medpace-study-expense-tracker',
    imports: [
        MdsAgGridComponent,
        MdsButtonComponent,
        MdsDateRangePickerComponent,
        MdsButtonToggleGroupComponent,
        MdsDividerComponent,
    ],
    providers: [TitleCasePipe, UndoRedoService, NotificationHubService],
    standalone: true,
    templateUrl: './study-expense-tracker.component.html',
    styleUrl: './study-expense-tracker.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyExpenseTrackerComponent implements OnDestroy {
    private readonly expenseTrackingService = inject(ExpenseTrackingService);
    private readonly studyStateService = inject(StudyStateService);
    private readonly userService = inject(UserServiceV2);
    private readonly datePipe = inject(dateFormatPipe);
    private readonly titlePipe = inject(TitleCasePipe);
    private readonly siteUrlService = inject(SiteService);
    private readonly currencyStateService = inject(CurrencyStateService);
    private readonly dialog = inject(MatDialog);
    private readonly undoRedoService = inject(UndoRedoService);
    private readonly notificationHub = inject(NotificationHubService);

    /** Flag to prevent recording undo actions during undo/redo operations */
    private isUndoRedoOperation = false;

    notificationGroup = computed(() => {
        const studyId = this.studyId();

        if (!studyId) return;
        return buildRequestSetGroup(studyId);
    });

    private readonly joinRequestSetGroup = effect(() => {
        const group = this.notificationGroup();

        if (!group) return;

        this.notificationHub.invokeJoinGroup(group);
    });

    private readonly requestChanges = signal<
        {
            action: LiveNotificationsActionEnum;
            requestType: RequestChangedNotification['requestType'];
            requestId: number;
            dto: StudyExpenseTrackingDataRowDto[];
        }[]
    >([]);

    // Resolves notifications in arrival order and appends each completed change to the processing queue.
    private readonly enqueueRequestChanges = this.notificationHub.requestChanged$
        .pipe(
            concatMap((notification) => {
                const changedRequest = {
                    action: notification.action,
                    requestType: notification.requestType,
                    requestId: notification.id,
                };

                //when deleted we dont need to check if exists in the db ,so we just check if it exists in set if yes then remove
                if (notification.action === LiveNotificationsActionEnum.Deleted) {
                    return of({ ...changedRequest, dto: [] as StudyExpenseTrackingDataRowDto[] });
                }

                return this.expenseTrackingService
                    .getDataByRequest(this.studyId(), notification.requestType, notification.id)
                    .pipe(
                        map((dto) => ({ ...changedRequest, dto })),
                        catchError(() => EMPTY)
                    );
            }),
            takeUntilDestroyed()
        )
        .subscribe((changedRequest) => this.requestChanges.update((changes) => [...changes, changedRequest]));

    // Consumes the oldest queued notification and removes it after applying its grid transaction.
    private readonly refreshOnRequestChanged = effect(() => {
        const changes = this.requestChanges();
        const gridApi = this.gridApi();
        const changedRequest = changes[0];
        if (!gridApi || !changedRequest) return;

        const currentRows: StudyExpenseTrackingDataRow[] = [];
        gridApi.forEachNode((node) => node.data && currentRows.push(node.data));

        if (changedRequest.action === LiveNotificationsActionEnum.Deleted) {
            const removedRows = currentRows.filter(
                (row) =>
                    row.requestId === changedRequest.requestId &&
                    String(row.requestType) === String(changedRequest.requestType)
            );
            gridApi.applyTransaction({ remove: removedRows });
        } else {
            const mappedRows = this.mapToModel(changedRequest.dto);
            if (changedRequest.dto.length > 0 && mappedRows.length === 0) return;

            const currentRowIds = new Set(currentRows.map((row) => row.rowId));
            const existingRows = mappedRows.filter((row) => currentRowIds.has(row.rowId));
            const newRows = mappedRows.filter((row) => !currentRowIds.has(row.rowId));

            gridApi.applyTransaction({ update: existingRows, add: newRows });
        }

        this.requestChanges.update((queuedChanges) => queuedChanges.slice(1));
    });

    public readonly studyId = input.required<number>();
    public readonly isNewWindow = input.required<boolean>();
    public readonly tableHeight = input<string>('75rem');

    private readonly availableStatuses = this.expenseTrackingService.getAvailableActionsDictionary();
    private readonly studyCountries = this.expenseTrackingService.getStudyCountries(this.studyId);
    private readonly visitNames = this.expenseTrackingService.getStudyVisitNames(this.studyId);
    private readonly availableExpenseTypes = toSignal(this.studyStateService.getAvailableExpenses());
    private readonly expenseTypes = this.expenseTrackingService.getStudyExpenseTypes(this.studyId);
    private readonly currencies = toSignal(this.currencyStateService.getCurrencies());
    private readonly paymentMerchants = this.expenseTrackingService.paymentMerchants;
    private readonly travelMerchants = this.expenseTrackingService.travelMerchants;

    private readonly requiredNonEmptyFields: StudyExpenseTrackingColumnId[] = [
        'statusId',
        'merchant',
        'expenseType',
        'receiptDate',
        'localCurrencyCode',
        'requestedAmount',
        'amountAuthorized',
    ];

    private readonly numberColumns: StudyExpenseTrackingColumnId[] = [
        'requestedAmount',
        'amountAuthorized',
        'totalUSD',
    ];

    private readonly dateColumns: StudyExpenseTrackingColumnId[] = ['receiptDate', 'transactionDate'];

    private readonly gridColumns = signal<Column<any>[] | undefined>(undefined);
    private readonly gridDataAsDto: WritableSignal<StudyExpenseTrackingDataRowDto[] | undefined> = signal(undefined);

    private pendingUpdates: CellEditRequestEvent<StudyExpenseTrackingDataRow>[] = [];
    private isProcessingScheduled = false;

    /** Flag to prevent onGridColumnsChanged from re-triggering effects during state restoration */
    private isRestoringState = false;

    private readonly setFilterState = effect(() => {
        const gridApi = this.gridApi();

        // don't use gridApi.getColumns() here, it may be null and filter will not be set
        // also it's not a signal, so it won't rerun the effect
        if (!this.gridData() || !this.gridColumns() || !this.colDefs()) return;

        const filterState = JSON.parse(localStorage.getItem('filterState')) as FilterModel;

        if (filterState) {
            gridApi.setFilterModel(filterState);
        }
    });

    private readonly setColumnState = effect(() => {
        const gridApi = this.gridApi();

        if (!this.gridData() || !this.gridColumns() || !gridApi?.getColumns()) return;

        const columnState = JSON.parse(localStorage.getItem('columnState')) as ColumnState[];

        if (columnState) {
            this.isRestoringState = true;
            try {
                gridApi.applyColumnState({
                    state: columnState,
                    applyOrder: true,
                });
            } finally {
                this.isRestoringState = false;
            }
        }
    });

    private readonly setDataFromStore = effect(() => {
        const storeData = JSON.parse(localStorage.getItem('setDataRange')) as SetQuery;

        if (storeData && !storeData.isDefault) {
            this.filterFormGroup.controls.startDatepickerControl.setValue(new Date(storeData.from));
            this.filterFormGroup.controls.endDatepickerControl.setValue(new Date(storeData.to));
            this.filterFormGroup.controls.isDefault.setValue(storeData.isDefault);
        } else this.setDefaultPreFilter();

        this.loadData();
    });

    public readonly gridApi: WritableSignal<GridApi<StudyExpenseTrackingDataRow> | undefined> = signal(undefined);

    public readonly gridData = computed(() => {
        const gridDataAsDto = this.gridDataAsDto();
        if (!gridDataAsDto) return undefined;

        return this.mapToModel(gridDataAsDto);
    });

    public readonly buttonsToggle: MdsButtonToggle[] = [
        { name: 'Payment In Process', value: 'Payment In Process', id: '1' },
        { name: 'Booking In Process', value: 'Booking In Process', id: '2' },
        { name: 'Pending PS Approval', value: 'Pending PS Approval', id: '3' },
        { name: 'Pending ER Submission', value: 'Pending ER Submission', id: '4' },
        { name: 'Pending Invoice', value: 'Pending Invoice', id: '5' },
    ];

    public readonly filterFormGroup = new FormGroup({
        startDatepickerControl: new FormControl<Date>(null, [
            Validators.required,
            afterDateValidator(
                minDateValue,
                `Date cannot be before ${dateTransform(this.datePipe, minDateValue.toString())}`
            ),
            beforeDateValidator(
                maxDateValue,
                `Date cannot be after ${dateTransform(this.datePipe, maxDateValue.toString())}`
            ),
        ]),
        endDatepickerControl: new FormControl<Date>(null, [
            Validators.required,
            afterDateValidator(
                minDateValue,
                `Date cannot be before ${dateTransform(this.datePipe, minDateValue.toString())}`
            ),
            beforeDateValidator(
                maxDateValue,
                `Date cannot be after ${dateTransform(this.datePipe, maxDateValue.toString())}`
            ),
        ]),
        isDefault: new FormControl<boolean>(false, { nonNullable: true }),
    });

    public readonly formControlToggleButtons = new FormControl<string[]>([]);

    // Column Definitions: Defines & controls grid columns.
    public readonly colDefs = computed(() => {
        const studyCountries = this.studyCountries.value();
        const availableStatuses = this.availableStatuses.value();
        const visitNames = this.visitNames.value();
        const expenseTypes = this.expenseTypes.value();
        const availableExpenses = this.availableExpenseTypes();
        const elevatedAccess = !!this.userService.signalPermissions.request.access.elevated();

        const currencies = this.currencies();
        const paymentMerchants = this.paymentMerchants.hasValue()
            ? [emptyMerchantOption, ...this.paymentMerchants.value()]
            : [];
        const travelMerchants = this.travelMerchants.hasValue()
            ? [emptyMerchantOption, ...this.travelMerchants.value()]
            : [];

        if (
            !studyCountries ||
            !availableStatuses ||
            !visitNames ||
            !expenseTypes ||
            !currencies ||
            !paymentMerchants ||
            !travelMerchants ||
            !availableExpenses
        )
            return undefined;

        const defs: StudyExpenseTrackerColDef<any, any>[] = [
            <any>{
                field: 'actions',
                cellClass: ['pt-[3px]'],
                width: 40,
                headerName: 'Add request',
                headerClass: '[&_.ag-header-cell-text]:text-transparent', // make the header text transparent
                filter: false,
                comparator: (
                    valueA: undefined,
                    valueB: undefined,
                    nodeA: IRowNode<StudyExpenseTrackingDataRow>,
                    nodeB: IRowNode<StudyExpenseTrackingDataRow>
                ) => {
                    if (isPossibleToAddRow(nodeA.data) && !isPossibleToAddRow(nodeB.data)) return -1;
                    else if (!isPossibleToAddRow(nodeA.data) && isPossibleToAddRow(nodeB.data)) return 1;
                    else return 0;
                },
                cellRenderer: CustomCellEditorButtonComponent,
                cellRendererParams: {
                    icon: 'add',
                    onClickAdd: (row: IRowNode) => {
                        this.addRow(row);
                    },
                    onClickRemove: (row: IRowNode) => {
                        this.removeNotSavedRow(row);
                    },
                },
                cellRendererSelector: this.columnAddRow,
            },
            <StudyExpenseTrackerColDef<number>>{
                field: 'statusId',
                headerName: 'Request Status',
                cellEditorPopup: true,
                cellClass: 'stringType',
                filter: 'agSetColumnFilter',
                suppressPaste: true,
                comparator: requestStatusComparator,
                cellRendererSelector(params: ICellRendererParams<StudyExpenseTrackingDataRow, string>) {
                    if (params.data.isDuplicate) {
                        return {
                            component: CustomCellRenderImgComponent,
                            params: {
                                src: '/../../assets/img/duplicate-icn.png',
                            },
                        };
                    }

                    return params.valueFormatted;
                },
                filterValueGetter: (value) => {
                    const rowData = value.data as StudyExpenseTrackingDataRow;
                    if (rowData.requestType === RequestTypesKeys.TRAVEL) {
                        const statusString = RequestStatusNames[TravelRequestStatus[rowData.statusId]];
                        return statusString;
                    } else {
                        const statusString = RequestStatusNames[PaymentRequestStatus[rowData.statusId]];
                        return statusString;
                    }
                },
                cellEditor: 'PaymentRequestStatusChangeModalCellEditorComponent',
                cellEditorPopupPosition: 'under',
                cellEditorSelector: (params) => {
                    const statusId = params.value;

                    if (params.data.requestType === RequestTypesKeys.TRAVEL) {
                        const statusDictionary = availableStatuses.travelActionsDictionary;
                        const availableOptions = statusDictionary[statusId];
                        if (!availableOptions) throw new Error('Status not found!');

                        return {
                            component: TravelRequestStatusChangeModalCellEditorComponent,
                            params: {
                                requestData: {
                                    requestId: params.data.requestId,
                                    patientId: params.data.patientId,
                                },
                            },
                        };
                    } else {
                        const statusDictionary = availableStatuses.paymentActionsDictionary;
                        const availableOptions = statusDictionary[statusId];

                        if (!availableOptions) throw new Error('Status not found!');

                        const nodes = new Array<IRowNode<StudyExpenseTrackingDataRow>>(); // prepare empty array for all rows from the grid
                        this.gridApi().forEachNode((node) => nodes.push(node)); // gets all rows from the grid and pushes to array
                        // need to calculate sum of amountAuthorized's
                        // if some expense's amountAuthorized is changed, it won't trigger a refresh of the grid (that would be too heavy).
                        // when changing status, it would show the old sum of authorized fields
                        const expenseRows = nodes.filter(
                            (node) =>
                                // get expenses from the same request
                                node.data.requestId === params.data.requestId &&
                                // must also match requestType - requestId is not unique across travel,reimbursement and stipend requests
                                node.data.requestType === params.data.requestType
                        );
                        const allExpensesShareCurrency = expenseRows.every(
                            (node) => node.data.localCurrencyCode === expenseRows.at(0).data.localCurrencyCode
                        );
                        let totalAuthorized = params.data.totalAuthorized; // if currencies mismatch, prepopulat it with the total authorized from db
                        if (allExpensesShareCurrency) {
                            totalAuthorized = expenseRows.reduce((prev, curr) => {
                                // sum it up
                                return prev + curr.data.amountAuthorized;
                            }, 0);
                        }

                        return {
                            component: PaymentRequestStatusChangeModalCellEditorComponent,
                            params: {
                                requestData: {
                                    requestId: params.data.requestId,
                                    requestType: params.data.requestType,
                                    totalAmount:
                                        params.data.requestType === RequestTypesKeys.STIPEND
                                            ? params.data.amountAuthorized // stipend has only one expense
                                            : totalAuthorized, // for reimbursement requests, pass it the sum of all expenses
                                    currency: params.data.totalCurrencyCode.value,
                                    siteId: params.data.siteId,
                                    patientId: params.data.patientId,
                                    totalAmountInUSD: params.data.totalUSD,
                                    totalAmountPatientsCurrency: params.data.totalPatientsCurrency,
                                    patientsCurrencyCode: params.data.patientsCurrencyCode,
                                },
                                values: availableOptions,
                                formatValue: (paymentStatusId) => {
                                    const statusString = RequestStatusNames[PaymentRequestStatus[paymentStatusId]];
                                    return statusString;
                                },
                            },
                        };
                    }
                },
                valueFormatter: (row) => {
                    if (row.data.requestType === RequestTypesKeys.TRAVEL) {
                        const statusString = RequestStatusNames[TravelRequestStatus[row.data.statusId]];
                        return statusString;
                    } else {
                        const statusString = RequestStatusNames[PaymentRequestStatus[row.data.statusId]];
                        return statusString;
                    }
                },
                valueSetter: (params) => {
                    return false; //block set data after unfocus. Change status logic is inside custom component
                },
                editable: (params) => isNotInFinalStatus(params.data),
            },
            {
                field: 'statusChangedAt',
                headerName: 'Date of last status change',
                comparator: dateComparator,
                valueFormatter: (row) => this.datePipe.transform(row.value, DateFormat.dateOnly, 'local'),
                valueGetter: (value) => {
                    let result = value.data.statusChangedAt
                        ? this.datePipe.transform(value.data.statusChangedAt, DateFormat.dateOnly, 'local')
                        : '';

                    return result;
                },
            },
            {
                field: 'projectId',
                headerName: 'Project ID',
                cellClass: 'stringType',
            },
            <StudyExpenseTrackerColDef<number>>{
                field: 'requestId',
                cellClass: 'stringType',
                headerName: 'Request ID',
                filterValueGetter: (params) => {
                    const requestType = params.data.requestType;
                    return getRequestIdentifier(params.data.requestId, requestType);
                },
                valueFormatter: (params) => {
                    const requestType = params.data.requestType;
                    return getRequestIdentifier(params.data.requestId, requestType);
                },
                cellRenderer: (params: ICellRendererParams<StudyExpenseTrackingDataRow, number, any>) => {
                    const requestType = params.data.requestType;
                    const displayValue = getRequestIdentifier(params.value, requestType);

                    const studyId = params.data.studyId;
                    const siteId = params.data.siteId;
                    const patientId = params.data.patientId;
                    const requestId = params.data.requestId;
                    const baseUrl = `${this.siteUrlService.getSiteBaseUrl()}/studies/${studyId}/sites/${siteId}/patients/${patientId}/requests/${requestId}/edit`; //?payment-travel=${paymentOrTravel}&stipend-oop=out-of-pocket`;

                    const queryParams = new URLSearchParams();

                    queryParams.set(
                        'payment-travel',
                        requestType === RequestTypesKeys.TRAVEL ? RequestTypes.TRAVEL : RequestTypes.PAYMENT
                    );

                    if (requestType === RequestTypesKeys.REIMBURSEMENT) {
                        queryParams.set('stipend-oop', 'out-of-pocket');
                    } else if (requestType === RequestTypesKeys.STIPEND) {
                        queryParams.set('stipend-oop', 'stipend');
                    }

                    const href = `${baseUrl}?${queryParams.toString()}`;
                    return `<a href="${href}" target="_blank" rel="noopener">${displayValue}</a>`;
                },
            },
            {
                field: 'siteNumber',
                headerName: 'Site #',
                cellClass: 'stringType',
            },
            {
                field: 'country',
                headerName: 'Country',
                cellClass: 'stringType',
            },
            {
                field: 'pcsId',
                headerName: 'PCS ID',
                valueFormatter: (row) =>
                    this.userService.signalPermissions.patient.pcsIdView() ? row.value : '***-***',
            },
            {
                field: 'patientNumber',
                headerName: 'Patient ID',
                cellClass: 'stringType',
            },
            <StudyExpenseTrackerColDef<VisitName>>{
                field: 'visitName',
                headerName: 'Visit Name',
                cellClass: 'stringType',
                comparator: visitNameComparator,
                filterValueGetter: (value) => value.data.visitName?.name,
                valueFormatter: (row) => row.value?.name,
            },
            <StudyExpenseTrackerColDef<string>>{
                field: 'visitStartDate',
                headerName: 'Visit Date',
                comparator: dateComparator,
                valueFormatter: (row) => this.datePipe.transform(row.value, DateFormat.dateOnly, 'local'),
                valueGetter: (value) =>
                    this.datePipe.transform(value.data.visitStartDate, DateFormat.dateOnly, 'local'),

                editable: false,
            },
            <StudyExpenseTrackerColDef<string>>{
                field: 'requestType',
                headerName: 'Request Type',
                cellClass: 'stringType',
                editable: false,
                valueFormatter: (row) => {
                    return this.titlePipe.transform(row.value);
                },
            },

            <StudyExpenseTrackerColDef<string, CustomCellEditorDatepickerComponent>>{
                field: 'receiptDate',
                headerName: 'Receipt Date',
                comparator: dateComparator,
                cellRendererSelector(params: ICellRendererParams<StudyExpenseTrackingDataRow, string>) {
                    if (
                        !params.data.receiptDate &&
                        isPaymentBeforePendingPSApproval(params.data) &&
                        isNotInFinalStatus(params.data) &&
                        params.data.requestType !== RequestTypesKeys.TRAVEL
                    ) {
                        return {
                            component: CustomCellRenderIconComponent,
                            params: {
                                icon: 'error',
                            },
                        };
                    }

                    return params.data.receiptDate;
                },
                valueFormatter: (row) => this.datePipe.transform(row.value, DateFormat.dateOnly, 'local'),
                cellEditor: CustomCellEditorDatepickerComponent,
                cellEditorParams: {
                    min: minDateValue,
                    max: maxDateValue,
                },
                valueGetter: (value) => {
                    let result = value.data.receiptDate
                        ? this.datePipe.transform(value.data.receiptDate, DateFormat.dateOnly, 'local')
                        : '';

                    return result;
                },
                cellClassRules: {
                    'empty-required-cell': (params) =>
                        !params.value &&
                        isPaymentBeforePendingPSApproval(params.data) &&
                        isNotInFinalStatus(params.data) &&
                        params.data.requestType !== RequestTypesKeys.TRAVEL,
                    'empty-cell': (params) =>
                        !params.value &&
                        !isPaymentBeforePendingPSApproval(params.data) &&
                        isNotInFinalStatus(params.data) &&
                        !isTravelWithReceipt(params.data),
                },
                editable: (params) => {
                    return (
                        (isNotInFinalStatus(params.data) && isPaymentBeforePendingPSApproval(params.data)) ||
                        isTravelWithReceipt(params.data)
                    );
                },
            },
            <StudyExpenseTrackerColDef<string, CustomCellEditorDatepickerComponent>>{
                field: 'transactionDate',
                headerName: 'Transaction Date',
                comparator: dateComparator,
                valueFormatter: (row) => this.datePipe.transform(row.value, DateFormat.dateOnly, 'local'),
                cellEditor: CustomCellEditorDatepickerComponent,
                cellEditorParams: {
                    min: minDateValue,
                    max: maxDateValue,
                },
                valueGetter: (value) =>
                    value.data.transactionDate
                        ? this.datePipe.transform(value.data.transactionDate, DateFormat.dateOnly, 'local')
                        : '',
                editable: (params) => {
                    return (
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                    );
                },
                cellClassRules: {
                    'empty-cell': (params) =>
                        !params.value &&
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data)),
                },
            },
            <
                StudyExpenseTrackerColDef<
                    MdsOptionGeneric<number>,
                    CustomCellEditorDropdownComponent<MdsOptionGeneric<number>>
                >
            >{
                field: 'merchant',
                headerName: 'Merchant',
                cellClass: 'stringType',
                suppressPaste: true,
                comparator: (valueA, valueB) => {
                    if (valueA.viewValue === valueB.viewValue) return 0;
                    return valueA.viewValue > valueB.viewValue ? 1 : -1;
                },
                filterValueGetter: (value) => {
                    return value.data.merchant?.viewValue;
                },
                cellEditorSelector: (params) => {
                    return {
                        component: CustomCellEditorDropdownComponent<MdsOptionGeneric<number>>,
                        params: {
                            values:
                                params.data.requestType === RequestTypesKeys.TRAVEL
                                    ? travelMerchants
                                    : paymentMerchants,
                            formatValue: (merchant) => merchant?.viewValue ?? '',
                        },
                    };
                },
                valueFormatter: (row) => {
                    return row.value?.viewValue ?? '';
                },
                editable: (params) => {
                    return (
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data)) &&
                        isNotInFinalStatus(params.data)
                    );
                },
            },
            <
                StudyExpenseTrackerColDef<
                    StudyExpense | TravelRequestEnum,
                    CustomCellEditorDropdownComponent<StudyExpense>
                >
            >{
                field: 'expenseType',
                headerName: 'Expense Type',
                cellClass: 'stringType',
                suppressPaste: true,
                comparator: expenseTypeComparator,
                cellRendererSelector(params: ICellRendererParams<StudyExpenseTrackingDataRow, string>) {
                    if (
                        !params.data.expenseType &&
                        params.data.requestType !== RequestTypesKeys.STIPEND &&
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                    ) {
                        return {
                            component: CustomCellRenderIconComponent,
                            params: {
                                icon: 'error',
                            },
                        };
                    }

                    return params.valueFormatted;
                },
                cellEditorSelector(params) {
                    const availableToSelect = availableExpenses.filter(
                        (ae) =>
                            ((!!params.data.expenseType &&
                                ae.expenseId === (params.data.expenseType as StudyExpense).expenseId) ||
                                expenseTypes.map((x) => x.expenseId).includes(ae.expenseId)) &&
                            ae.expenseId !== ExpenseTypeEnum.Mileage
                    );
                    return {
                        component: CustomCellEditorDropdownComponent<StudyExpense>,
                        params: {
                            values: availableToSelect,
                            formatValue: (expenseType) => (expenseType as StudyExpense)?.expenseDefaultValue ?? '',
                        },
                    };
                },
                filterValueGetter: (value) => {
                    return value.data.requestType === RequestTypesKeys.TRAVEL
                        ? this.titlePipe.transform(
                              getTravelRequestEnumStringValue(value.data.expenseType as TravelRequestEnum)
                          )
                        : ((value.data.expenseType as StudyExpense)?.expenseDefaultValue ?? '');
                },
                valueFormatter: (row) => {
                    return row.data.requestType === RequestTypesKeys.TRAVEL
                        ? this.titlePipe.transform(
                              getTravelRequestEnumStringValue(row.data.expenseType as TravelRequestEnum)
                          )
                        : ((row.data.expenseType as StudyExpense)?.expenseDefaultValue ?? '');
                },
                cellClassRules: {
                    'empty-required-cell': (params) =>
                        !params.value &&
                        params.data.requestType !== RequestTypesKeys.STIPEND &&
                        isNotInFinalStatus(params.data) &&
                        isPaymentBeforePendingPSApproval(params.data),
                },
                editable: (params) => {
                    return (
                        isNotMileage(params.data) &&
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) ||
                            params.data.requestType === RequestTypesKeys.TRAVEL)
                    );
                },
            },
            <StudyExpenseTrackerColDef<CurrencyViewModel, CustomCellEditorDropdownComponent<CurrencyViewModel>>>{
                field: 'localCurrencyCode',
                headerName: 'Local Currency',
                cellClass: 'stringType',
                suppressPaste: true,
                comparator: localCurrencyComparator,
                cellRendererSelector(params: ICellRendererParams<StudyExpenseTrackingDataRow, string>) {
                    if (
                        !params.data.localCurrencyCode &&
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                    ) {
                        return {
                            component: CustomCellRenderIconComponent,
                            params: {
                                icon: 'error',
                            },
                        };
                    }

                    return params.data.localCurrencyCode;
                },
                filterValueGetter: (value) => {
                    return value.data.localCurrencyCode?.isocode;
                },
                cellEditorSelector(params) {
                    return {
                        component: CustomCellEditorDropdownComponent<CurrencyViewModel>,
                        params: {
                            values: currencies,
                            formatValue: (currency) => {
                                return currency?.isocode?.toUpperCase();
                            },
                        },
                    };
                },
                valueFormatter: (row) => {
                    return row.value?.isocode?.toUpperCase() ?? '';
                },
                cellClassRules: {
                    'empty-required-cell': (params) =>
                        !params.value &&
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data)),
                },
                editable: (params) => {
                    return (
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                    );
                },
            },
            <StudyExpenseTrackerColDef<number, CustomCellEditorInputComponent<number>>>{
                field: 'requestedAmount',
                headerName: 'Requested (local currency)',
                cellEditor: 'agNumberCellEditor',
                cellEditorParams: {
                    min: minNumberValue,
                    max: maxNumberValue,
                },
                cellRendererSelector(params: ICellRendererParams<StudyExpenseTrackingDataRow, string>) {
                    if (
                        (params.data.requestedAmount == null || params.data.requestedAmount?.toString() == '') &&
                        isPaymentBeforePendingPSApproval(params.data) &&
                        isNotInFinalStatus(params.data) &&
                        params.data.requestType !== RequestTypesKeys.TRAVEL
                    ) {
                        return {
                            component: CustomCellRenderIconComponent,
                            params: {
                                icon: 'error',
                            },
                        };
                    }

                    return params.data.requestedAmount;
                },
                filterValueGetter: (row) => {
                    if (row.data.requestedAmount != null && row.data.requestedAmount?.toString() != '')
                        return row.data.requestedAmount.toFixed(2);
                    else return '';
                },
                valueFormatter: (row) => {
                    if (row.value != null && row.value?.toString() != '') return row.value.toFixed(2);
                    else return '';
                },
                valueGetter: (value) => {
                    if (value.data.requestedAmount != null && value.data.requestedAmount?.toString() != '')
                        return +value.data.requestedAmount;
                    else return '';
                },
                editable: (params) => {
                    return (
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                    );
                },
                cellClass: 'currencyFormat',
                cellClassRules: {
                    'empty-required-cell': (params) =>
                        (params.value == null || params.value?.toString() == '') &&
                        isPaymentBeforePendingPSApproval(params.data) &&
                        isNotInFinalStatus(params.data),
                    'empty-cell': (params) =>
                        (params.value == null || params.value?.toString() == '') &&
                        !isPaymentBeforePendingPSApproval(params.data) &&
                        isNotInFinalStatus(params.data) &&
                        params.data.requestType !== RequestTypesKeys.TRAVEL,
                },
            },
            <StudyExpenseTrackerColDef<number, CustomCellEditorInputComponent<number>>>{
                field: 'amountAuthorized',
                headerName: 'Authorized (local currency)',
                cellEditor: 'agNumberCellEditor',
                cellEditorParams: {
                    min: minNumberValue,
                    max: maxNumberValue,
                },
                cellRendererSelector(params: ICellRendererParams<StudyExpenseTrackingDataRow, number>) {
                    if (
                        (params.data.amountAuthorized == null || params.data.amountAuthorized?.toString() == '') &&
                        params.data.requestType === RequestTypesKeys.REIMBURSEMENT &&
                        isPaymentBeforePendingPSApproval(params.data)
                    ) {
                        return {
                            component: CustomCellRenderIconComponent,
                            params: {
                                icon: 'error',
                            },
                        };
                    }

                    return params.valueFormatted;
                },
                valueGetter: (value) => {
                    if (value.data.amountAuthorized != null && value.data.amountAuthorized?.toString() != '')
                        return +value.data.amountAuthorized;
                    else return '';
                },
                filterValueGetter: (row) => {
                    if (row.data.amountAuthorized != null && row.data.amountAuthorized?.toString() != '')
                        return row.data.amountAuthorized.toFixed(2);
                    else return '';
                },
                valueFormatter: (row) => {
                    // for requests we want to see 0's for other requests if there is 0 we want to see empty string
                    if (row.value != null && row.value?.toString() != '') return row.value.toFixed(2);
                    else return '';
                },
                editable: (params) => {
                    return (
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                    );
                },
                cellClass: 'currencyFormat',
                cellClassRules: {
                    'empty-cell': (params) =>
                        (params.value?.toString() == '' || params.value == null) &&
                        isNotInFinalStatus(params.data) &&
                        (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data)) &&
                        params.data.requestType !== RequestTypesKeys.REIMBURSEMENT,
                    'empty-required-cell': (params) => {
                        return (
                            (params.value?.toString() == '' || params.value == null) &&
                            params.data.requestType === RequestTypesKeys.REIMBURSEMENT &&
                            (isPaymentBeforePendingPSApproval(params.data) || isTravelWithReceipt(params.data))
                        );
                    },
                },
            },
            <StudyExpenseTrackerColDef<number, CustomCellEditorInputComponent<number>>>{
                field: 'totalUSD',
                headerName: 'Authorized (USD)',
                cellEditor: 'agNumberCellEditor',
                filterValueGetter: (row) => {
                    if (row.data.totalUSD != null && row.data.totalUSD?.toString() != '')
                        return row.data.totalUSD.toFixed(2);
                    else return '';
                },
                valueGetter: (value) => {
                    if (value.data.totalUSD != null && value.data.totalUSD?.toString() != '')
                        return +value.data.totalUSD;
                    else return '';
                },
                valueFormatter: (row) => {
                    // for travel requests we want to see 0's for other requests if there is 0 we want to see empty string
                    if (row.value != null && row.value?.toString() != '')
                        //for travel requests we want to check if it is >= 0 or other not null/undefined value
                        return row.value.toFixed(2);
                    else return '';
                },
                editable: false,
                cellClass: 'currencyFormat$',
            },
            <StudyExpenseTrackerColDef<number, CustomCellEditorInputComponent<number>>>{
                hide: true,
                field: 'totalPatientsCurrency',
                headerName: "Authorized (patient's currency)",
                cellEditor: 'agNumberCellEditor',
                filterValueGetter: (row) => {
                    if (row.data.totalPatientsCurrency != null && row.data.totalPatientsCurrency?.toString() != '')
                        return row.data.totalPatientsCurrency.toFixed(2);
                    else return '';
                },
                valueGetter: (value) => {
                    if (value.data.totalPatientsCurrency != null && value.data.totalPatientsCurrency?.toString() != '')
                        return +value.data.totalPatientsCurrency;
                    else return '';
                },
                valueFormatter: (row) => {
                    if (row.value != null && row.value?.toString() != '')
                        return `${row.value.toFixed(2)} (${row.data.patientsCurrencyCode})`;
                    else return '';
                },
                editable: false,
                cellClass: 'currencyFormat$',
            },
            <StudyExpenseTrackerColDef<string, CustomCellEditorInputComponent<string>>>{
                field: 'vendorId',
                headerName: 'Vendor ID',
                cellClass: 'stringType',
                cellEditorSelector(params) {
                    return {
                        component: CustomCellEditorInputComponent<string>,
                        params: {
                            inputType: 'text',
                        },
                    };
                },
                editable: false,
            },
            <StudyExpenseTrackerColDef<string, CustomCellEditorInputComponent<string>>>{
                field: 'requestNotes',
                headerName: 'Expense Notes',
                cellEditor: 'agTextCellEditor',
                cellClass: 'stringType',
                cellEditorParams: {
                    maxLength: 500,
                },
                editable: (params) => isRequestNotesEditable(params.data, elevatedAccess),
                cellClassRules: {
                    'empty-cell': (params) => !params.value && isRequestNotesEditable(params.data, elevatedAccess),
                },
            },
            <StudyExpenseTrackerColDef<string, CustomCellEditorInputComponent<string>>>{
                field: 'erTrackingId',
                headerName: 'Expense Report Tracking ID',
                cellClass: 'stringType',
                cellEditor: 'agTextCellEditor',
                cellEditorParams: {
                    maxLength: 200,
                },
                cellClassRules: {
                    'empty-cell': (params) =>
                        (isPayment(params.data) && !params.value) ||
                        (!params.value && isTravelWithReceipt(params.data)),
                },
                editable: (params) => isPayment(params.data) || isTravelWithReceipt(params.data),
            },
            <StudyExpenseTrackerColDef<string, CustomCellEditorInputComponent<string>>>{
                field: 'erReportingPeriod',
                headerName: 'ER Reporting Period',
                cellClass: 'stringType',
                cellEditor: 'agTextCellEditor',
                cellEditorParams: {
                    maxLength: 200,
                },
                cellClassRules: {
                    'empty-cell': (params) =>
                        (isPayment(params.data) && !params.value) ||
                        (!params.value && isTravelWithReceipt(params.data)),
                },
                editable: (params) => isPayment(params.data) || isTravelWithReceipt(params.data),
            },
            <StudyExpenseTrackerColDef<string, CustomCellEditorInputComponent<string>>>{
                field: 'dateRequestAddedToER',
                headerName: 'Date Request Added to ER',
                editable: false,
                suppressFloatingFilterButton: true,
                comparator: dateComparator,
                valueFormatter: (row) => this.datePipe.transform(row.value, DateFormat.dateOnly, 'local'),
                valueGetter: (row) =>
                    this.datePipe.transform(row.data.dateRequestAddedToER, DateFormat.dateOnly, 'local'),
            },
            {
                field: 'pcsUser',
                headerName: 'Processed By',
                cellClass: 'stringType',
            },
        ];

        // Payment requests in Pending PS Approval / PQ Processing are read-only in every column except Status,
        // which must stay editable so the status can still be changed.
        // Read-only cells are not highlighted when empty, so the empty-cell highlight rules are suppressed too.
        const gatedDefs = defs.map((def): StudyExpenseTrackerColDef<any, any> => {
            const editable = def.editable;
            const classRules = def.cellClassRules;
            const gated: StudyExpenseTrackerColDef<any, any> = { ...def };

            if (classRules) {
                gated.cellClassRules = Object.fromEntries(
                    Object.entries(classRules).map(([className, rule]) => [
                        className,
                        typeof rule === 'function'
                            ? (params: any) => !isPaymentReadOnlyStatus(params.data) && rule(params)
                            : rule,
                    ])
                );
            }

            if (editable && def.field !== 'statusId') {
                gated.editable = (params: any) =>
                    !isPaymentReadOnlyStatus(params.data) &&
                    (typeof editable === 'function' ? editable(params) : editable);
            }

            return gated;
        });

        const canViewPcsId = this.userService.signalPermissions.patient.pcsIdView();
        return canViewPcsId ? gatedDefs : gatedDefs.filter((def) => def.field !== 'pcsId');
    });

    public readonly defaultColDef: ColDef = {
        cellEditorPopup: false,
        cellEditorPopupPosition: 'under',
        editable: false,
        enableCellChangeFlash: true,
        contextMenuItems: [
            {
                name: 'Add',
                action: this.addNewRow.bind(this),
            },
            'copy',
            'paste',
            {
                name: 'Export data',
                action: this.onBtExport.bind(this),
            },
        ],
        filter: 'agSetColumnFilter',
        cellDataType: false,
        suppressHeaderMenuButton: true,
    };

    public readonly gridOptions: GridOptions = {
        context: this,
        suppressLastEmptyLineOnPaste: true,
        cellSelection: {
            enableHeaderHighlight: true,
        },
        sideBar: {
            ...AgGridDefaults.defaultSidebar,
        },
        statusBar: {
            statusPanels: [
                {
                    statusPanel: AgGridStatusBarComponent,
                },
            ],
        },
        rowSelection: {
            mode: 'multiRow',
        },
        readOnlyEdit: true,
        getRowId: (params) => {
            return params.data.rowId;
        },
        rowClassRules: {
            'new-row': (params) => params.data?.expenseId === 0,
        },
        excelStyles: <ExcelStyle[]>[
            {
                id: 'currencyFormat',
                numberFormat: {
                    format: '0.00',
                },
            },
            {
                id: 'currencyFormat$',
                numberFormat: {
                    format: '0.00',
                },
            },
            {
                id: 'stringType',
                dataType: 'String',
            },
        ],
        onGridPreDestroyed: this.onGridPreDestroyed,
        onGridColumnsChanged: (eventCols) => {
            if (this.isRestoringState) return;
            const columns = eventCols.api.getColumns();
            this.gridColumns.set(columns);
        },
        onRowDataUpdated: (event) => {
            event.api.refreshCells({ columns: ['statusId'], force: true });
        },
        onCellEditRequest: this.onCellEditRequest,
        processCellFromClipboard: (params) => this.processCellFromClipboard(params),
    };

    public ngOnDestroy(): void {
        // Clear undo/redo history when component is destroyed
        this.undoRedoService.clear();
        const group = this.notificationGroup();
        if (group) {
            this.notificationHub.invokeLeaveGroup(group);
        }
    }

    /**
     * Handle keyboard shortcuts for undo (Ctrl+Z) and redo (Ctrl+Y or Ctrl+Shift+Z)
     */
    @HostListener('window:keydown', ['$event'])
    public handleKeyboardEvent(event: KeyboardEvent): void {
        // Check if we're inside an input element to avoid interfering with native undo/redo
        const activeElement = document.activeElement;
        const isInputElement =
            activeElement instanceof HTMLInputElement ||
            activeElement instanceof HTMLTextAreaElement ||
            activeElement?.getAttribute('contenteditable') === 'true';

        if (isInputElement) {
            return;
        }

        // Undo: Ctrl+Z (without Shift)
        if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === 'z') {
            event.preventDefault();
            this.performUndo();
            return;
        }

        // Redo: Ctrl+Y or Ctrl+Shift+Z
        if (
            (event.ctrlKey && event.key.toLowerCase() === 'y') ||
            (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'z')
        ) {
            event.preventDefault();
            this.performRedo();
            return;
        }
    }

    public onGridReady(params: GridReadyEvent): void {
        this.gridApi.set(params.api);
    }

    public resetFilters(): void {
        if (this.gridApi()) this.gridApi().setFilterModel(null);
        this.setDefaultPreFilter();
        this.loadData();
        this.saveFilterStateToLocalStorage(undefined);
    }

    public datePickerDateChanged() {
        // for some reason, when changing end date this.filterFormGroup.valid = false and loadData() doesn't update the data. Hence a quick updateValueAndValidity() on changed controls
        this.filterFormGroup.controls.startDatepickerControl.updateValueAndValidity();
        this.filterFormGroup.controls.endDatepickerControl.updateValueAndValidity();
        this.filterFormGroup.controls.isDefault.setValue(false);
        this.loadData();
    }

    public quickFilterChangeEvent(event: MatButtonToggleChange): void {
        const filterModel =
            event.value?.length < 1
                ? null
                : {
                      filterType: 'set',
                      values: event.value,
                  };
        this.gridApi()
            .setColumnFilterModel('statusId', filterModel)
            .then(() => {
                this.gridApi().onFilterChanged();
            });
    }

    public onFilterChanged(): void {
        const filterModel = this.gridApi().getColumnFilterModel('statusId') as SetFilterModel;
        this.formControlToggleButtons.setValue(filterModel?.values?.length > 0 ? filterModel?.values : []);

        this.saveFilterStateToLocalStorage(this.gridApi()?.getFilterModel());
    }

    public onColumnChanged() {
        this.saveColumnStateToLocalStorage(this.gridApi()?.getColumnState());
    }

    public onBtExport() {
        if (
            !this.gridApi() ||
            this.gridApi().getDisplayedRowCount() < 1 ||
            !this.gridData() ||
            this.gridData().length < 1
        ) {
            this.dialog.open<MessageDialogComponent, MessageDialogData, ModalResult>(MessageDialogComponent, {
                data: {
                    title: `Information`,
                    bodyText: 'There is no data available to export.',
                    showCancelButton: false,
                    okayButtonLabel: 'Close',
                },
                ...getModalConfig(ModalSize.Small),
            });
            return;
        }
        this.exportDataToExcel();
    }

    private setDefaultPreFilter(): void {
        const today = new Date();
        const monthsBefore = new Date();
        monthsBefore.setDate(today.getDate() - 90);

        this.filterFormGroup.controls.startDatepickerControl.setValue(monthsBefore);
        this.filterFormGroup.controls.endDatepickerControl.setValue(today);
        this.filterFormGroup.controls.isDefault.setValue(true);
    }

    private performUndo(): void {
        const action = this.undoRedoService.undo();
        if (!action || action.changes.length === 0) {
            return;
        }

        this.applyBatchUndoRedo(action.changes, true);
    }

    private performRedo(): void {
        const action = this.undoRedoService.redo();
        if (!action || action.changes.length === 0) {
            return;
        }

        this.applyBatchUndoRedo(action.changes, false);
    }

    private applyBatchUndoRedo(changes: BatchEditAction[], isUndoOperation: boolean): void {
        const gridApi = this.gridApi();

        if (!gridApi || !this.studyId()) {
            return;
        }

        // History survives reloads, so re-check the current status: rows that became read-only
        // (Pending PS Approval / PQ Processing) must not be changed by undo/redo, except for the Status column.
        const currentRows = new Map<string, StudyExpenseTrackingDataRow>();
        gridApi.forEachNode((node) => {
            if (node.data) {
                currentRows.set(`${node.data.requestType}:${node.data.expenseId}`, node.data);
            }
        });

        const changesToApply = changes
            .filter((change) => {
                const currentRow = currentRows.get(`${change.requestType}:${change.expenseId}`);
                return !isPaymentReadOnlyStatus(currentRow) || change.updatedCellIds.every((id) => id === 'statusId');
            })
            .map((change) => {
                const newRow = isUndoOperation ? change.oldRow : change.newRow;
                const oldRow = isUndoOperation ? change.newRow : change.oldRow;
                return { ...change, newRow, oldRow };
            });

        if (changesToApply.length === 0) {
            return;
        }

        this.patchBatchExpenses(changesToApply, true, true);
    }

    private loadData(): void {
        if (!this.filterFormGroup.valid) {
            this.filterFormGroup.markAllAsTouched();
            return;
        }

        const setQuery: SetQuery = {
            from: moment(this.filterFormGroup.controls.startDatepickerControl.value)
                .format()
                .substring(0, this.filterFormGroup.controls.startDatepickerControl.value.toISOString().indexOf('T')),
            to: moment(this.filterFormGroup.controls.endDatepickerControl.value)
                .format()
                .substring(0, this.filterFormGroup.controls.endDatepickerControl.value.toISOString().indexOf('T')),
            isDefault: this.filterFormGroup.controls.isDefault.value,
        };

        combineLatest([
            this.expenseTrackingService.getData(this.studyId(), setQuery).pipe(startWith(undefined)),
            timer(0, this.expenseTrackingService.timeToShowLoadingIndicatorMs),
        ])
            .pipe(
                tap(([data, count]) => {
                    if (!data && count > 0) {
                        this.gridApi()?.setGridOption('activeOverlay', 'agLoadingOverlay');
                    } else if (data) {
                        this.gridDataAsDto.set(data);
                        this.gridApi()?.setGridOption('activeOverlay', undefined);
                    }
                }),
                takeWhile(([data, _]) => !data, true)
            )
            .subscribe();

        this.savePreFiltersToLocalStorage(setQuery);
    }

    private savePreFiltersToLocalStorage(setQuery: SetQuery) {
        const dataToStore = setQuery;
        localStorage.setItem('setDataRange', Object.keys(dataToStore).length > 0 ? JSON.stringify(dataToStore) : null);
    }

    private addRow(row: IRowNode<StudyExpenseTrackingDataRow>) {
        if (!row?.data || !isPossibleToAddRow(row.data)) {
            return;
        }

        let newItem: StudyExpenseTrackingDataRow = deepClone(row.data);
        const guid = guidGenerator();
        newItem.rowId = guid;
        newItem.receiptDate = undefined;
        newItem.transactionDate = row.data.transactionDate;
        newItem.merchant = row.data.requestType === RequestTypesKeys.TRAVEL ? emptyMerchantOption : row.data.merchant;
        newItem.expenseType = null;
        newItem.localCurrencyCode = null;
        newItem.requestedAmount = undefined;
        newItem.amountAuthorized = undefined;
        newItem.requestNotes = undefined;
        newItem.expenseId = 0;

        const newItems = [newItem];
        const newRowIndex = row.rowIndex + 1;

        this.gridApi().applyTransaction(<RowDataTransaction>{
            add: newItems,
            addIndex: newRowIndex,
        });

        const newRow = this.gridApi().getRowNode(guid);
        this.gridApi().startEditingCell(<StartEditingCellParams>{
            rowIndex: newRow.rowIndex,
            colKey: 'receiptDate',
        });
    }

    private isValidFields(newItem: StudyExpenseTrackingDataRow, rowIndex: number): boolean {
        if (newItem.receiptDate === undefined || newItem.receiptDate === null) {
            this.gridApi().startEditingCell(<StartEditingCellParams>{
                rowIndex: rowIndex,
                colKey: 'receiptDate',
            });
            return false;
        }

        if (newItem.expenseType === undefined || newItem.expenseType === null) {
            this.gridApi().startEditingCell(<StartEditingCellParams>{
                rowIndex: rowIndex,
                colKey: 'expenseType',
            });
            return false;
        }

        if (newItem.localCurrencyCode === undefined || newItem.localCurrencyCode === null) {
            this.gridApi().startEditingCell(<StartEditingCellParams>{
                rowIndex: rowIndex,
                colKey: 'localCurrencyCode',
            });
            return false;
        }

        if (newItem.requestedAmount === undefined || newItem.requestedAmount === null) {
            this.gridApi().startEditingCell(<StartEditingCellParams>{
                rowIndex: rowIndex,
                colKey: 'requestedAmount',
            });
            return false;
        }

        if (
            newItem.requestType === RequestTypesKeys.REIMBURSEMENT &&
            (newItem.amountAuthorized === undefined || newItem.amountAuthorized === null)
        ) {
            this.gridApi().startEditingCell(<StartEditingCellParams>{
                rowIndex: rowIndex,
                colKey: 'amountAuthorized',
            });
            return false;
        }

        return true;
    }

    private mapRowDataToDTO(rowData: StudyExpenseTrackingDataRow): CreateEditSETDataRowDto {
        const receiptDate = !!rowData.receiptDate
            ? (moment(rowData.receiptDate) ?? moment(rowData.receiptDate, 'dd-mmm-yyyy'))
            : null;
        const translationDate = !!rowData.transactionDate
            ? (moment(rowData.transactionDate) ?? moment(rowData.transactionDate, 'dd-mmm-yyyy'))
            : null;
        return {
            rowId: rowData.rowId,
            statusId: rowData.statusId,
            statusChangedAt: rowData.statusChangedAt, //DateOnly
            receiptDate: !!receiptDate ? receiptDate.format().substring(0, receiptDate.format().indexOf('T')) : null,
            transactionDate: !!translationDate
                ? translationDate.format().substring(0, translationDate.format().indexOf('T'))
                : null,
            merchant: rowData.merchant?.value,
            expenseType:
                rowData.requestType === RequestTypesKeys.TRAVEL
                    ? (rowData.expenseType as TravelRequestEnum)
                    : ((rowData.expenseType as StudyExpense)?.expenseId ?? 0),
            localCurrencyCode: rowData.localCurrencyCode?.isocode,
            requestedAmount: rowData.requestedAmount,
            amountAuthorized: rowData.amountAuthorized,
            totalUSD: rowData.totalUSD,
            totalPatientsCurrency: rowData.totalPatientsCurrency,
            exchangeRateDate: rowData.exchangeRateDate ?? null,
            requestNotes: rowData.requestNotes,
            erTrackingId: rowData.erTrackingId,
            erReportingPeriod: rowData.erReportingPeriod,
        };
    }

    private createNewExpenses(dataToUpdate: StudyExpenseTrackingDataRow[]): Observable<BatchSetAddResponse[]> {
        const batchAdd: BatchSetAddExpense[] = dataToUpdate.map((data) => ({
            requestId: data.requestId,
            expense: this.mapRowDataToDTO(data),
        }));

        return this.expenseTrackingService.createReimbursementExpenses(batchAdd).pipe(
            take(1),
            tap((batchAddResponse) =>
                batchAddResponse.forEach((b) => {
                    const rowNode = this.gridApi().getRowNode(b.rowId);
                    const rowData = rowNode.data;
                    rowNode.updateData({ ...rowData, expenseId: b.expenseId });
                })
            )
        );
    }

    private onCellEditRequest(
        cell: CellEditRequestEvent<StudyExpenseTrackingDataRow, unknown, StudyExpenseTrackerComponent>
    ) {
        // method called from gridOptions, need to use cell.context instead of this
        // moved logic to separate method to not accidentally call `this` instead of `cell.context`
        cell.context.handleCellEditRequest(cell);
    }

    private handleCellEditRequest(
        cell: CellEditRequestEvent<StudyExpenseTrackingDataRow, unknown, StudyExpenseTrackerComponent>
    ) {
        // Status is changed only by the status modal editor (via its Confirm button, followed by loadData).
        // Dismissing the modal by clicking outside makes ag-grid commit the unconfirmed value, so ignore it.
        if (cell.column.getColId() === 'statusId') return;

        // Add to queue
        this.pendingUpdates.push(cell);

        // Schedule processing if not already scheduled
        if (!this.isProcessingScheduled) {
            this.isProcessingScheduled = true;
            queueMicrotask(() => {
                this.processPendingUpdates();
            });
        }
    }

    private processPendingUpdates() {
        const updates = [...this.pendingUpdates];
        this.pendingUpdates = [];
        this.isProcessingScheduled = false;

        if (updates.length === 0) return;

        this.handleUpdates(updates);
    }

    private handleUpdates(cells: CellEditRequestEvent<StudyExpenseTrackingDataRow>[]) {
        // Group cells by rowId
        const rowCells = new Map<string, CellEditRequestEvent<StudyExpenseTrackingDataRow>[]>();

        for (const cell of cells) {
            const rowId = cell.data.rowId;
            if (!rowCells.has(rowId)) {
                rowCells.set(rowId, []);
            }
            rowCells.get(rowId).push(cell);
        }

        const { transactions, rowUpdateInfos } = this.getBatchTransactionsAndUpdateInfos(rowCells);
        const { batchExpenses, validatedNewExpenses } = this.createBatchExpenses(rowUpdateInfos);

        // Apply UI updates immediately (optimistic)
        if (transactions.length > 0) {
            cells[0].api.applyTransaction({ update: transactions });
        }

        // Recalculate totalUSD for rows where amountAuthorized changed, then proceed with backend update.
        // Rows that need conversion but have no receipt date are left out, so the exclusion logic below reverts them.
        this.recalculateTotalUSD(rowUpdateInfos, batchExpenses, cells[0].api)
            .pipe(
                switchMap((rowIdsMissingConversionDate) =>
                    this.preApplyCheck({
                        batchExpenses: batchExpenses.filter((be) => !rowIdsMissingConversionDate.has(be.oldRow.rowId)),
                        validatedNewExpenses,
                    })
                )
            )
            .subscribe(({ proceed, filteredBatchExpenses, filteredNewExpenses }) => {
                if (proceed) {
                    // Find request keys of directly excluded expenses
                    const includedRowIds = new Set(filteredBatchExpenses.map((be) => be.oldRow.rowId));
                    const excludedRequestKeys = new Set<string>();
                    for (const be of batchExpenses) {
                        if (!includedRowIds.has(be.oldRow.rowId)) {
                            const info = rowUpdateInfos.get(be.oldRow.rowId);
                            if (info) {
                                excludedRequestKeys.add(
                                    `${info.dataToUpdate.requestType}_${info.dataToUpdate.requestId}`
                                );
                            }
                        }
                    }

                    // Also exclude sibling expenses from the same request
                    const fullyFilteredBatchExpenses = filteredBatchExpenses.filter((be) => {
                        const info = rowUpdateInfos.get(be.oldRow.rowId);
                        if (!info) return true;
                        const key = `${info.dataToUpdate.requestType}_${info.dataToUpdate.requestId}`;
                        return !excludedRequestKeys.has(key);
                    });

                    // Revert all rows from excluded requests
                    const excludedOriginalRows = Array.from(rowUpdateInfos.values())
                        .filter((info) => {
                            const key = `${info.dataToUpdate.requestType}_${info.dataToUpdate.requestId}`;
                            return excludedRequestKeys.has(key);
                        })
                        .map((info) => info.originalData);
                    if (excludedOriginalRows.length > 0) {
                        cells[0].api.applyTransaction({ update: excludedOriginalRows });
                    }

                    this.executeBackendUpdate(fullyFilteredBatchExpenses, filteredNewExpenses);
                } else {
                    // Revert UI changes if user cancelled
                    const originalRows = Array.from(rowUpdateInfos.values()).map((info) => info.originalData);
                    if (originalRows.length > 0) {
                        cells[0].api.applyTransaction({ update: originalRows });
                    }
                }
            });
    }
    createBatchExpenses(rowUpdateInfos: Map<string, SetRowInfo<StudyExpenseTrackingDataRow>>) {
        const batchExpenses: BatchEditAction[] = [];
        const newExpenses: {
            rowCell: CellEditRequestEvent<StudyExpenseTrackingDataRow>;
            dataToUpdate: StudyExpenseTrackingDataRow;
        }[] = [];

        for (const info of rowUpdateInfos.values()) {
            const { dataToUpdate, originalData } = info;

            if (dataToUpdate.requestType === RequestTypesKeys.REIMBURSEMENT && dataToUpdate.expenseId === 0) {
                newExpenses.push({ rowCell: info.cells[0], dataToUpdate });
                continue;
            }

            // Skip if only statusId was changed
            const hasNonStatusChanges = info.cells.some((cell) => cell.column.getColId() !== 'statusId');
            if (!hasNonStatusChanges) continue;

            const anyValueChanged = compare(originalData, dataToUpdate).length > 0;
            if (!anyValueChanged) continue;

            let properExpenseId: number | null = null;

            switch (dataToUpdate.requestType) {
                case RequestTypesKeys.STIPEND:
                    properExpenseId = dataToUpdate.requestId;
                    break;
                case RequestTypesKeys.REIMBURSEMENT:
                    properExpenseId = dataToUpdate.expenseId;
                    break;
                case RequestTypesKeys.TRAVEL:
                    properExpenseId = dataToUpdate.travelReceiptId;
                    break;
                default:
                    throw new Error('Unknown request type');
            }

            batchExpenses.push({
                expenseId: properExpenseId,
                requestType: dataToUpdate.requestType,
                oldRow: this.mapRowDataToDTO(originalData),
                newRow: this.mapRowDataToDTO(dataToUpdate),
                updatedCellIds: info.cells.map((cell) => cell.column.getColId()),
            });
        }

        const validatedNewExpenses: StudyExpenseTrackingDataRow[] = [];

        for (const newExpense of newExpenses) {
            const { rowCell, dataToUpdate } = newExpense;
            if (!this.isValidFields(dataToUpdate, rowCell.node.rowIndex)) {
                continue;
            }
            validatedNewExpenses.push(dataToUpdate);
        }

        return { batchExpenses, validatedNewExpenses };
    }
    preApplyCheck({
        batchExpenses,
        validatedNewExpenses,
    }: {
        batchExpenses: BatchEditAction[];
        validatedNewExpenses: StudyExpenseTrackingDataRow[];
    }): Observable<{
        proceed: boolean;
        filteredBatchExpenses: BatchEditAction[];
        filteredNewExpenses: StudyExpenseTrackingDataRow[];
    }> {
        // Check for duplicates before proceeding
        if (batchExpenses.length > 0 || validatedNewExpenses.length > 0) {
            // Only include rows where duplicate-relevant fields were changed.
            // Stipend/Travel: no SET-editable fields affect duplicates, so skip existing rows entirely.
            // Reimbursement: only expenseType and requestedAmount affect duplicate detection.
            const duplicateRelevantFields: string[] = ['expenseType', 'requestedAmount'];
            const duplicateRelevantBatchExpenses = batchExpenses.filter((be) => {
                if (be.expenseId === 0) return true;
                if (be.requestType !== RequestTypesKeys.REIMBURSEMENT) return false;
                return be.updatedCellIds.some((id) => duplicateRelevantFields.includes(id));
            });

            // Prepare batch actions for duplicate checking
            const allBatchForDuplicateCheck: BatchEditAction[] = [...duplicateRelevantBatchExpenses];

            // Add new expenses converted to batch actions for duplicate checking
            if (validatedNewExpenses.length > 0) {
                const newExpenseBatchActions: BatchEditAction[] = validatedNewExpenses.map((expense) => ({
                    expenseId:
                        expense.requestType === RequestTypesKeys.STIPEND
                            ? expense.requestId
                            : expense.requestType === RequestTypesKeys.REIMBURSEMENT
                              ? 0
                              : (expense.travelReceiptId ?? 0),
                    requestId: expense.requestId,
                    requestType: expense.requestType,
                    oldRow: this.mapRowDataToDTO(expense),
                    newRow: this.mapRowDataToDTO(expense),
                    updatedCellIds: [],
                }));
                allBatchForDuplicateCheck.push(...newExpenseBatchActions);
            }
            return this.expenseTrackingService.checkForDuplicates(allBatchForDuplicateCheck).pipe(
                switchMap((checkResult) => {
                    if (checkResult.hasDuplicates) {
                        return this.showDuplicateDialog(checkResult.potentialDuplicates).pipe(
                            map((dialogResult) => {
                                if (dialogResult?.result === ModalResult.Okay) {
                                    // Filter batch to only include selected expenses
                                    const selectedIds = new Set(
                                        dialogResult.selectedExpenseIds.map((s) => `${s.requestType}_${s.expenseId}`)
                                    );
                                    // Include non-duplicate expenses + user-selected duplicate expenses
                                    const duplicateExpenseKeys = new Set(
                                        checkResult.potentialDuplicates.map((d) => `${d.requestType}_${d.expenseId}`)
                                    );
                                    const filteredBatchExpenses = batchExpenses.filter((be) => {
                                        const key = `${be.requestType}_${be.expenseId}`;
                                        return !duplicateExpenseKeys.has(key) || selectedIds.has(key);
                                    });
                                    const filteredNewExpenses = validatedNewExpenses.filter((expense) => {
                                        const expenseId =
                                            expense.requestType === RequestTypesKeys.STIPEND
                                                ? expense.requestId
                                                : expense.requestType === RequestTypesKeys.REIMBURSEMENT
                                                  ? 0
                                                  : (expense.travelReceiptId ?? 0);
                                        const key = `${expense.requestType}_${expenseId}`;
                                        return !duplicateExpenseKeys.has(key) || selectedIds.has(key);
                                    });
                                    return {
                                        proceed: filteredBatchExpenses.length > 0 || filteredNewExpenses.length > 0,
                                        filteredBatchExpenses,
                                        filteredNewExpenses,
                                    };
                                }
                                return { proceed: false, filteredBatchExpenses: [], filteredNewExpenses: [] };
                            })
                        );
                    } else {
                        return of({
                            proceed: true,
                            filteredBatchExpenses: batchExpenses,
                            filteredNewExpenses: validatedNewExpenses,
                        });
                    }
                })
            );
        }

        return of({
            proceed: true,
            filteredBatchExpenses: batchExpenses,
            filteredNewExpenses: validatedNewExpenses,
        });
    }

    private getDisplayedColumnKeys(): string[] {
        const displayedColumns = this.gridApi().getAllDisplayedColumns();
        const columnKeys = displayedColumns.map((col) => col.getColId());
        return columnKeys;
    }

    private getFileName(): string {
        const today = new Date();

        const date = this.datePipe.transform(today, DateFormat.dateOnly, 'local');
        const firstRow = this.gridApi().getDisplayedRowAtIndex(this.gridApi().getFirstDisplayedRowIndex());
        return `${date} - ${firstRow.data.projectId}`;
    }

    private getParams: () => ExcelExportParams = () => ({
        processCellCallback(params: ProcessCellForExportParams<StudyExpenseTrackingDataRow, any>): string {
            const mappedValue = mapToExportValue(params);
            return mappedValue;
        },
        fileName: this.getFileName(),
        columnKeys: this.getDisplayedColumnKeys().filter((col) => col !== 'pcsId' && col !== 'actions'),
    });

    private exportDataToExcel() {
        this.gridApi().exportDataAsExcel(this.getParams());
    }

    private saveFilterStateToLocalStorage(filterState: FilterModel | null): void {
        localStorage.setItem(
            'filterState',
            filterState && Object.keys(filterState).length > 0 ? JSON.stringify(filterState) : null
        );
    }

    private saveColumnStateToLocalStorage(columnState: ColumnState[] | null): void {
        localStorage.setItem(
            'columnState',
            columnState && Object.keys(columnState).length > 0 ? JSON.stringify(columnState) : null
        );
    }

    private onGridPreDestroyed(event: GridPreDestroyedEvent): void {
        const filterState = event.api.getFilterModel();
        const columnState = event.api.getColumnState();

        const component = event.context as StudyExpenseTrackerComponent;

        component.saveColumnStateToLocalStorage(columnState);
        component.saveFilterStateToLocalStorage(filterState);
    }

    private removeNotSavedRow(row: IRowNode) {
        let rowData: StudyExpenseTrackingDataRow = row.data;
        const rowId = rowData.rowId;

        this.gridApi().applyTransaction(<ServerSideTransaction>{
            remove: [{ rowId: rowId }],
        });
    }

    private columnAddRow(params: ICellRendererParams) {
        const rowData = params.data as StudyExpenseTrackingDataRow;
        if (!isPossibleToAddRow(rowData)) {
            if (rowData.expenseId === 0) {
                return {
                    component: CustomCellEditorButtonComponent,
                    params: {
                        icon: 'remove',
                    },
                }; //empty
            } else return {};
        } else {
            return; //return component from cell render parameter
        }
    }

    private addNewRow(params: IMenuActionParams) {
        this.addRow(params.node);
    }

    // replaces rows matching a changed rowId and appends the rest, so new expenses not yet in gridDataAsDto are added too
    private mergeChangedRows(
        currentRows: StudyExpenseTrackingDataRowDto[],
        changedRows: StudyExpenseTrackingDataRowDto[]
    ): StudyExpenseTrackingDataRowDto[] {
        const changedRowIds = new Set(changedRows.map((row) => row.rowId));
        const untouchedRows = currentRows.filter((row) => !changedRowIds.has(row.rowId));

        return [...untouchedRows, ...changedRows];
    }

    private mapToModel(dto: StudyExpenseTrackingDataRowDto[]): StudyExpenseTrackingDataRow[] {
        const studyCountries = this.studyCountries.value();
        const visitNames = this.visitNames.value();
        const expenseTypes = this.availableExpenseTypes();
        const currencies = this.currencies();
        const paymentMerchants = this.paymentMerchants.hasValue()
            ? [emptyMerchantOption, ...this.paymentMerchants.value()]
            : [];
        const travelMerchants = this.travelMerchants.hasValue()
            ? [emptyMerchantOption, ...this.travelMerchants.value()]
            : [];
        if (!studyCountries || !visitNames || !expenseTypes || !currencies || !paymentMerchants || !travelMerchants) {
            return [];
        }

        const mappedData = dto.map<StudyExpenseTrackingDataRow>((dto) => ({
            ...dto,
            visitName: visitNames.find((v) => v.id === dto.visitNameId) ?? emptyVisitName,
            visitStartDateLocalTime: dto.visitStartDate ? moment(dto.visitStartDate).toISOString() : '', // convert dateonly string to isostring in local tz, 2025-01-01 -> 2025-01-01T00:00:00+02
            transactionDate: dto.transactionDate ? moment(dto.transactionDate).toISOString() : '',
            receiptDate: dto.receiptDate ? moment(dto.receiptDate).toISOString() : '',
            expenseType:
                dto.requestType === RequestTypesKeys.TRAVEL
                    ? dto.travelRequestType
                    : (expenseTypes.find((e) => e.expenseId === dto.expenseType) ?? null),
            localCurrencyCode:
                currencies.find(
                    (currency) => currency.isocode?.toLowerCase() === dto.localCurrencyCode?.toLowerCase()
                ) ?? null,
            totalCurrencyCode:
                currencies.find(
                    (currency) => currency.isocode?.toLowerCase() === dto.totalCurrencyCode?.toLowerCase()
                ) ?? null,
            dateRequestAddedToER: dto.dateRequestAddedToER ? moment(dto.dateRequestAddedToER).toISOString() : '',
            requestType: dto.requestType,
            merchant:
                dto.requestType === RequestTypesKeys.TRAVEL
                    ? travelMerchants.find((m) =>
                          !dto.merchant ? m.value === emptyMerchantOption.value : m.value === dto.merchant
                      )
                    : paymentMerchants.find((m) =>
                          !dto.merchant ? m.value === emptyMerchantOption.value : m.value === dto.merchant
                      ),
        }));

        return mappedData;
    }

    private getBatchTransactionsAndUpdateInfos(
        cellsGroupedByRowId: Map<string, CellEditRequestEvent<StudyExpenseTrackingDataRow>[]>
    ): {
        transactions: StudyExpenseTrackingDataRow[];
        rowUpdateInfos: Map<string, SetRowInfo<StudyExpenseTrackingDataRow>>;
    } {
        const transactions: StudyExpenseTrackingDataRow[] = [];
        const rowUpdateInfos = new Map<string, SetRowInfo<StudyExpenseTrackingDataRow>>();

        // Process each row's updates
        for (const [rowId, rowCells] of cellsGroupedByRowId.entries()) {
            // Merge all cell updates for this row
            let dataToUpdate: StudyExpenseTrackingDataRow = { ...rowCells[0].data };
            const originalData = rowCells[0].data;
            for (const cell of rowCells) {
                const requiredFieldExpense = this.requiredNonEmptyFields.some(
                    (field) => field === cell.column.getColId()
                );

                const emptyValue =
                    cell.newValue === null ||
                    cell.newValue === undefined ||
                    (requiredFieldExpense && cell.newValue === '');

                if (emptyValue) {
                    if (requiredFieldExpense) {
                        continue;
                    } else {
                        dataToUpdate[cell.column.getColId()] = undefined;
                    }
                } else {
                    dataToUpdate[cell.column.getColId()] = cell.newValue;
                }
            }

            transactions.push(dataToUpdate);
            rowUpdateInfos.set(rowId, {
                cells: rowCells,
                dataToUpdate,
                originalData,
            });
        }
        return { transactions, rowUpdateInfos };
    }

    private recalculateTotalUSD(
        rowUpdateInfos: Map<string, SetRowInfo<StudyExpenseTrackingDataRow>>,
        batchExpenses: BatchEditAction[],
        gridApi: GridApi<StudyExpenseTrackingDataRow>
    ): Observable<Set<string>> {
        const rowIdsMissingConversionDate = new Set<string>();
        const batchRequests: ConvertAuthorizedAmountRequest[] = [];
        const batchRowData: { dataToUpdate: StudyExpenseTrackingDataRow; shouldConvertPatientsCurrency: boolean }[] =
            [];

        for (const info of rowUpdateInfos.values()) {
            const changedColIds = info.cells.map((cell) => cell.column.getColId());
            const { dataToUpdate } = info;
            const requestType = dataToUpdate.requestType;

            const shouldRecalculate =
                changedColIds.includes('amountAuthorized') ||
                changedColIds.includes('localCurrencyCode') ||
                ((requestType === RequestTypesKeys.REIMBURSEMENT || requestType === RequestTypesKeys.TRAVEL) &&
                    changedColIds.includes('receiptDate'));

            if (!shouldRecalculate) continue;
            const currencyCode = dataToUpdate.localCurrencyCode?.isocode;
            const amountAuthorized = dataToUpdate.amountAuthorized;

            if (amountAuthorized == null || !currencyCode) continue;

            if (currencyCode === 'USD') {
                dataToUpdate.totalUSD = amountAuthorized;
                const batchExpense = batchExpenses.find((be) => be.oldRow.rowId === dataToUpdate.rowId);
                if (batchExpense) {
                    batchExpense.newRow.totalUSD = null; // USD rows send null
                }
                gridApi.applyTransaction({ update: [dataToUpdate] });
            } else {
                const patientCurrencyCode = dataToUpdate.patientsCurrencyCode ?? null;
                const shouldConvertPatientsCurrency =
                    (requestType === RequestTypesKeys.REIMBURSEMENT || requestType === RequestTypesKeys.STIPEND) &&
                    !!patientCurrencyCode &&
                    patientCurrencyCode !== currencyCode;

                const date = toExchangeRateDate(dataToUpdate.receiptDate);
                if (!date) {
                    rowIdsMissingConversionDate.add(dataToUpdate.rowId);
                    continue;
                }

                batchRequests.push({
                    authorizedAmount: amountAuthorized,
                    expenseCurrency: currencyCode,
                    date,
                    patientCurrency: shouldConvertPatientsCurrency ? patientCurrencyCode! : undefined,
                });
                batchRowData.push({ dataToUpdate, shouldConvertPatientsCurrency });
            }
        }

        if (batchRequests.length === 0) {
            return of(rowIdsMissingConversionDate);
        }

        return this.expenseTrackingService.batchConvertAuthorizedAmount(batchRequests).pipe(
            take(1),
            map((responses) => {
                for (let i = 0; i < responses.length; i++) {
                    const response = responses[i];
                    const { dataToUpdate, shouldConvertPatientsCurrency } = batchRowData[i];
                    const batchExpense = batchExpenses.find((be) => be.oldRow.rowId === dataToUpdate.rowId);

                    if (response.authorizedAmountUsd != null) {
                        dataToUpdate.totalUSD = response.authorizedAmountUsd;
                        if (batchExpense) {
                            batchExpense.newRow.totalUSD = response.authorizedAmountUsd;
                        }
                    }

                    dataToUpdate.exchangeRateDate = response.exchangeRateDate;
                    if (batchExpense) {
                        batchExpense.newRow.exchangeRateDate = response.exchangeRateDate;
                    }

                    if (shouldConvertPatientsCurrency && response.authorizedAmountPatientsCurrency != null) {
                        dataToUpdate.totalPatientsCurrency = response.authorizedAmountPatientsCurrency;
                        if (batchExpense) {
                            batchExpense.newRow.totalPatientsCurrency = response.authorizedAmountPatientsCurrency;
                        }
                    }
                }
                gridApi.applyTransaction({ update: batchRowData.map((r) => r.dataToUpdate) });
                return rowIdsMissingConversionDate;
            })
        );
    }

    private showDuplicateDialog(potentialDuplicates: PotentialDuplicateRequestDto[]) {
        const dialogData: DuplicateDialogData = {
            potentialDuplicates,
        };

        const dialogRef = this.dialog.open<DuplicateDialogComponent, DuplicateDialogData, DuplicateDialogResult>(
            DuplicateDialogComponent,
            {
                ...getModalConfig(ModalSize.Small),
                data: dialogData,
            }
        );

        return dialogRef.afterClosed();
    }

    private executeBackendUpdate(
        batchExpenses: BatchEditAction[],
        validatedNewExpenses: StudyExpenseTrackingDataRow[]
    ): void {
        this.createNewExpenses(validatedNewExpenses).subscribe(() => {
            if (batchExpenses.length > 0) {
                // Record undo actions before backend update (skip for undo/redo operations)
                if (!this.isUndoRedoOperation) {
                    this.undoRedoService.pushAction({ changes: batchExpenses });
                }
                const cellIdsRequiringRefreshOnUpdate = [
                    'visitName',
                    'visitStartDate',
                    'expenseType',
                    'requestedAmount',
                    'localCurrencyCode',
                ];
                const hasModifiedCellThatRequiresRefresh = batchExpenses.some((be) => {
                    return be.updatedCellIds.some((cellId) => cellIdsRequiringRefreshOnUpdate.includes(cellId));
                });
                const hasCreatedNewExpenses = validatedNewExpenses.length > 0;
                const forceRefresh = hasModifiedCellThatRequiresRefresh || hasCreatedNewExpenses;
                this.patchBatchExpenses(batchExpenses, forceRefresh);
            }

            if (validatedNewExpenses.length > 0) {
                this.loadData();
            }
        });
    }

    private patchBatchExpenses(
        batchExpenses: BatchEditAction[],
        forceRefresh: boolean = false,
        reloadUpdated: boolean = false
    ): void {
        this.expenseTrackingService
            .patchBatchExpenses(this.studyId(), batchExpenses)
            .pipe(take(1))
            .subscribe({
                complete: () =>
                    this.refreshGrid(
                        batchExpenses.map((be) => ({ requestType: be.requestType, updatedCellIds: be.updatedCellIds })),
                        forceRefresh,
                        reloadUpdated
                            ? batchExpenses.map((be) => ({ expenseId: be.expenseId, requestType: be.requestType }))
                            : batchExpenses
                                  .filter(
                                      (be) =>
                                          be.oldRow.localCurrencyCode !== be.newRow.localCurrencyCode &&
                                          be.newRow.localCurrencyCode === 'USD'
                                  )
                                  .map((be) => ({ expenseId: be.expenseId, requestType: be.requestType }))
                    ),
            });
    }

    private refreshGrid(
        updatedCellIdsPerRequest: { requestType: RequestTypesKeys; updatedCellIds: string[] }[],
        forceRefresh: boolean,
        expenseIdentifiers: ExpenseIdentifier[]
    ) {
        if (
            forceRefresh ||
            updatedCellIdsPerRequest.some((request) =>
                request.updatedCellIds.some(
                    (changedColId) =>
                        changedColId === 'transactionDate' ||
                        (changedColId === 'merchant' && request.requestType === RequestTypesKeys.REIMBURSEMENT) ||
                        changedColId === 'erReportingPeriod' ||
                        changedColId === 'erTrackingId'
                )
            )
        ) {
            this.loadData();
        } else if (expenseIdentifiers.length > 0) {
            this.updateRows(expenseIdentifiers);
        }
    }

    private updateRows(expenseIdentifiers: ExpenseIdentifier[]) {
        combineLatest([
            this.expenseTrackingService.getByExpenseIdentifiers(expenseIdentifiers).pipe(startWith(undefined)),
            timer(0, this.expenseTrackingService.timeToShowLoadingIndicatorMs),
        ])
            .pipe(
                tap(([data, count]) => {
                    if (!data && count > 0) {
                        this.gridApi()?.setGridOption('activeOverlay', 'agLoadingOverlay');
                    } else if (data) {
                        const mapped = this.mapToModel(data);
                        this.gridApi()?.applyTransaction({ update: mapped });

                        this.gridApi()?.setGridOption('activeOverlay', undefined);
                    }
                }),
                takeWhile(([data, _]) => !data, true)
            )
            .subscribe();
    }

    private processCellFromClipboard(params: ProcessCellForExportParams<StudyExpenseTrackingDataRow>): unknown {
        const oldValue = params.node.data[params.column.getColId()];

        const columnType: string = params.column.getColDef().cellEditor?.name ?? params.column.getColDef().cellEditor;

        switch (columnType) {
            case 'agTextCellEditor':
                return parseTextField(params.value, params.column);
            case 'agNumberCellEditor':
                return parseNumberField(oldValue, params.value, params.column);
            case 'CustomCellEditorDatepickerComponent':
                return parseDateField(oldValue, params.value, params.column);
        }

        return params.value;
    }
}
